import {
	useEffect,
	useRef,
} from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@tokamap/core/ui/style.css';
import type {
	TokaMapConfig,
	TokaMapInstance,
} from '@tokamap/core';
import { createTokaMap } from '@tokamap/core';

/**
 * Point d'accès console/debug volontairement public (pas de garde `import.meta.env.DEV`) : taper
 * `tokamap` dans les devtools doit retrouver l'instance et permettre d'appeler ses méthodes
 * (`tokamap.history.undo()`, `tokamap.mapAdapter.startDrawingPolygon()`, ...). Les tests e2e
 * (Playwright) s'appuient dessus aussi, mais ce n'est plus leur seule raison d'être.
 */
declare global {
	interface Window {
		tokamap?: TokaMapInstance;
	}
}

export default function TokaMap({ config = {}, onReady }: {
	config?: TokaMapConfig;
	onReady?: (instance: TokaMapInstance) => void
}) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const startedRef = useRef(false);

	useEffect(() => {
		// Garde contre le double-appel d'effet de React.StrictMode (dev) : cette app est montée une
		// seule fois pour toute la durée de vie de la page — pas de vrai cycle unmount/remount à
		// supporter —, donc on ignore la 2e invocation plutôt que d'ajouter un teardown complet
		// (FpsMeter, dans `@tokamap/core`, n'expose pas de destroy()).
		if (startedRef.current || !containerRef.current) return;
		startedRef.current = true;

		createTokaMap(containerRef.current, config).then((instance) => {
			window.tokamap = instance;

			// Accueil dans la console — uniquement en editMode (BESOINS.md §4) : en visualisation
			// seule, il n'y a rien à "outiller" pour un visiteur final, ce message serait du bruit.
			if (config.editMode) {
				console.log(
					'%cTokamap%c — tapez %ctokamap%c dans la console pour accéder à l\'instance ' +
					'(mapAdapter, shapeStore, history, commands) et appeler ses méthodes. Voir ' +
					'tokamap.commands pour la liste des commandes pilotables depuis la console.',
					'font-weight: bold; color: #ff9800;',
					'',
					'font-family: monospace; background: #222; color: #0f0; padding: 1px 4px; border-radius: 2px;',
					'',
				);
				// Objet indexé par `signature` plutôt qu'un tableau : `console.table` affiche alors
				// l'exemple d'appel en première colonne (déjà copiable tel quel), au lieu d'un simple
				// index numérique sans valeur informative.
				console.table(
					Object.fromEntries(
						instance.commands.map(({ signature, description }) => [signature, { description }]),
					),
				);
			}

			onReady?.(instance);
		});
	}, [config, onReady]);

	// `createTokaMap` ne pose `container.dataset.tokaTheme` qu'une fois, à la construction (voir la
	// garde StrictMode ci-dessus — cet effet ne relance jamais `createTokaMap`) : sans ce 2e effet,
	// basculer clair/sombre en cours de session (ex. réglage de l'app) laisserait la carte figée sur
	// le thème initial jusqu'au prochain rechargement complet de la page.
	useEffect(() => {
		if (containerRef.current) containerRef.current.dataset.tokaTheme = config.theme ?? 'light';
	}, [config.theme]);

	return <div id="app" ref={containerRef} />;
}
